import Link from "next/link";
import {
  ArrowUpRight,
  Briefcase,
  Clock,
  MapPin,
  Users,
} from "lucide-react";
import type { QaJobSummary } from "@/lib/jobs/types";
import {
  formatAtsName,
  formatExperienceShort,
  formatHeadcountBadge,
  formatLocationLabel,
  formatRelativePosted,
} from "@/lib/jobs/utils";
import { cn } from "@/lib/utils";
import { CompanyWordmark } from "./company-wordmark";

function isFreshPost(postedAt: string | null): boolean {
  if (!postedAt) return false;
  const ts = Date.parse(postedAt);
  if (Number.isNaN(ts)) return false;
  return Date.now() - ts <= 48 * 60 * 60 * 1000;
}

function workplaceBadgeClass(workplaceType: string): string {
  const norm = workplaceType.toLowerCase();
  if (norm === "remote") {
    return "border-[#0a7653]/25 bg-[#e8fedf] text-[#07533d]";
  }
  if (norm === "hybrid") {
    return "border-[#cbd5e1] bg-[#f8fafc] text-[#334155]";
  }
  return "border-[#d8dde3] bg-[#f4f6f8] text-[#475569]";
}

export function JobCard({
  job,
  compact = false,
  onSelectSkill,
  onSelectCompany,
}: {
  job: QaJobSummary;
  compact?: boolean;
  onSelectSkill?: (skill: string) => void;
  onSelectCompany?: (company: string) => void;
}) {
  const detailHref = `/qa-jobs/${job.slug}`;
  const companyFilterHref = `/qa-jobs?q=${encodeURIComponent(job.companyName)}`;
  const locationLabel = formatLocationLabel(job.city, job.country);
  const expLabel = formatExperienceShort(job.experienceLevel, job.minYears, job.maxYears);
  const headcountBadge = formatHeadcountBadge(job.headcountEst);
  const postedLabel = formatRelativePosted(job.postedAt);
  const fresh = isFreshPost(job.postedAt);
  const atsLabel = formatAtsName(job.atsType);

  return (
    <article className="group relative rounded-[16px] border border-[#dfe3e8] bg-white px-5 pt-3.5 pb-5 shadow-[0_4px_16px_rgba(15,23,42,0.035)] transition duration-150 hover:border-[#94a3b8] hover:shadow-[0_12px_30px_rgba(15,23,42,0.075)] sm:px-6 sm:pt-4 sm:pb-5.5">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        {/* Left: Signature Company Symbol + Wordmark + Role Title + Meta */}
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1.5">
            <CompanyWordmark
              companyName={job.companyName}
              verified={job.companyVerified}
              href={companyFilterHref}
              onClick={
                onSelectCompany
                  ? (e) => {
                      e.preventDefault();
                      onSelectCompany(job.companyName);
                    }
                  : undefined
              }
              variant={compact ? "cardCompact" : "card"}
            />

            {headcountBadge && !compact && (
              <span className="inline-flex items-center gap-1 rounded-md border border-[#dde1e6] bg-[#f8f9fb] px-2 py-0.5 text-[11.5px] font-medium text-[#525b65]">
                <Users size={11.5} aria-hidden="true" />
                {headcountBadge}
              </span>
            )}

            <span
              className={cn(
                "inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[11.5px] font-semibold",
                fresh
                  ? "border border-[#0a7653]/20 bg-[#e8fedf]/80 text-[#07533d]"
                  : "text-[#64748b]",
              )}
            >
              <Clock size={11.5} aria-hidden="true" />
              {postedLabel}
            </span>
          </div>

          <h2 className="mt-2 font-display text-[18.5px] font-bold leading-snug tracking-[-.02em] text-[#14171a] sm:text-[20px]">
            <Link
              prefetch={false}
              href={detailHref}
              className="transition-colors group-hover:text-[#0a7653] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-green"
            >
              {job.title}
            </Link>
          </h2>

          {/* Structured attributes row */}
          <div className="mt-2.5 flex flex-wrap items-center gap-2 text-xs">
            <span
              className={cn(
                "inline-flex items-center rounded-full border px-2.5 py-0.5 font-semibold",
                workplaceBadgeClass(job.workplaceType),
              )}
            >
              {job.workplaceType}
            </span>

            <span className="inline-flex items-center gap-1 rounded-full border border-[#dde1e6] bg-white px-2.5 py-0.5 font-medium text-[#374151]">
              <MapPin size={12} className="shrink-0 text-[#64748b]" aria-hidden="true" />
              <span className="max-w-[240px] truncate">{locationLabel}</span>
            </span>

            <span className="inline-flex items-center gap-1 rounded-full border border-[#dde1e6] bg-white px-2.5 py-0.5 font-medium text-[#374151]">
              <Briefcase size={12} className="shrink-0 text-[#64748b]" aria-hidden="true" />
              {expLabel}
            </span>

            <span className="inline-flex items-center rounded-full border border-[#dde1e6] bg-[#f8f9fb] px-2.5 py-0.5 font-medium text-[#4b5563]">
              {job.qaCategory}
            </span>
          </div>
        </div>

        {/* Right: Primary CTAs */}
        <div className="flex shrink-0 items-center gap-2 sm:flex-col sm:items-end sm:pt-0.5">
          <Link
            prefetch={false}
            href={detailHref}
            className="inline-flex h-10 flex-1 items-center justify-center rounded-[10px] border-[1.5px] border-ink bg-ink px-4 text-[13px] font-semibold text-white transition hover:bg-[#292929] sm:flex-none"
          >
            View Details
          </Link>
          {job.applyUrl && !compact && (
            <a
              href={job.applyUrl}
              target="_blank"
              rel="noopener noreferrer nofollow"
              className="inline-flex h-9 items-center justify-center gap-1 rounded-[9px] border border-[#d5dae1] bg-white px-3 text-xs font-semibold text-[#374151] transition hover:border-[#64748b] hover:bg-[#f8fafc] hover:text-ink"
              aria-label={`Apply for ${job.title} at ${job.companyName} via ${atsLabel}`}
            >
              <span>Apply · {atsLabel}</span>
              <ArrowUpRight size={13} aria-hidden="true" />
            </a>
          )}
        </div>
      </div>

      {/* Snippet + Extracted QA Stack */}
      {!compact && (job.descriptionSnippet || job.skills.length > 0) && (
        <div className="mt-4 border-t border-[#e5e7eb] pt-3.5">
          {job.descriptionSnippet && (
            <p className="line-clamp-2 text-[13.5px] leading-6 text-[#4b5563]">
              {job.descriptionSnippet}
            </p>
          )}

          {job.skills.length > 0 && (
            <div className="mt-3 flex flex-wrap items-center gap-1.5" aria-label="Detected skills">
              {job.skills.map((skill) => (
                <a
                  key={skill}
                  href={`/qa-jobs?q=${encodeURIComponent(skill)}`}
                  onClick={
                    onSelectSkill
                      ? (e) => {
                          e.preventDefault();
                          onSelectSkill(skill);
                        }
                      : undefined
                  }
                  className="inline-flex items-center rounded-md border border-[#d5dae1] bg-[#f8fafc] px-2.5 py-0.5 text-[11.5px] font-semibold text-[#334155] transition hover:border-[#0a7653]/45 hover:bg-[#e8fedf] hover:text-[#07533d]"
                >
                  {skill}
                </a>
              ))}
            </div>
          )}
        </div>
      )}
    </article>
  );
}
